import { useState, useEffect } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Gift, Copy, Share2, CheckCircle2, Users } from "lucide-react";
import { toast } from "sonner";

export default function ReferralCard({ compact = false }) {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    API.get("/referrals/mine").then(({ data }) => setData(data)).catch(() => setData(null));
  }, []);

  if (!data || !data.referral_code) return null;

  const shareUrl = `${window.location.origin}/register?ref=${data.referral_code}`;
  const reward = data.reward_amount || 20;

  const handleCopy = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success(`${label} copied!`);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async () => {
    const shareData = {
      title: "Join GLPFlo",
      text: `Get a $${reward} off coupon with my GLPFlo referral code ${data.referral_code}`,
      url: shareUrl,
    };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch { /* user cancelled */ }
    } else {
      handleCopy(shareUrl, "Share link");
    }
  };

  const { rewarded, pending } = data.counts;
  const remaining = data.remaining ?? (data.cap - rewarded);

  // Compact variant — used in the patient profile sidebar where the full
  // hero card was overpowering the page. ~1/3 the visual weight.
  if (compact) {
    return (
      <div className="rounded-xl p-3 bg-gradient-to-br from-purple-600 to-violet-700 text-white shadow-sm relative overflow-hidden" data-testid="referral-card-compact">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
            <Gift className="w-4 h-4" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold truncate" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Refer a friend — get ${reward}
            </p>
            <p className="text-[11px] text-purple-100 truncate">
              Code <span className="font-mono font-bold tracking-wider">{data.referral_code}</span> · {rewarded} rewarded{pending ? ` · ${pending} pending` : ""}
            </p>
          </div>
          <a
            href="/referrals"
            className="bg-white text-purple-700 hover:bg-purple-50 h-8 px-3 text-xs shrink-0 inline-flex items-center rounded-md font-medium"
            data-testid="referrals-page-link"
          >
            <Share2 className="w-3 h-3 mr-1" /> View hub
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl p-6 bg-gradient-to-br from-purple-600 via-purple-700 to-violet-800 text-white shadow-lg shadow-purple-200 relative overflow-hidden" data-testid="referral-card">
      {/* Decorative blob */}
      <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-white/10 blur-2xl" />
      <div className="absolute -bottom-10 -left-10 w-32 h-32 rounded-full bg-white/5 blur-2xl" />

      <div className="relative">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-10 h-10 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center">
            <Gift className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-lg" style={{ fontFamily: "'Outfit', sans-serif" }}>Refer a friend — get ${reward}</h3>
            <p className="text-purple-100 text-xs">They get a ${reward} off coupon too. Win-win.</p>
          </div>
        </div>

        <div className="mt-5 bg-white/10 backdrop-blur rounded-xl p-4 border border-white/20">
          <p className="text-xs text-purple-200 uppercase tracking-wider mb-1">Your referral code</p>
          <div className="flex items-center gap-2">
            <span className="font-mono text-2xl font-bold tracking-widest" data-testid="my-referral-code">{data.referral_code}</span>
            <Button onClick={() => handleCopy(data.referral_code, "Code")} size="sm" variant="ghost" className="ml-auto text-white hover:bg-white/20" data-testid="copy-referral-code-btn">
              {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        <div className="flex gap-2 mt-3">
          <Button onClick={handleShare} className="flex-1 bg-white text-purple-700 hover:bg-purple-50" data-testid="share-referral-btn">
            <Share2 className="w-4 h-4 mr-2" /> Share link
          </Button>
          <Button onClick={() => handleCopy(shareUrl, "Link")} variant="outline" className="border-white/40 text-white hover:bg-white/10" data-testid="copy-referral-link-btn">
            <Copy className="w-4 h-4" />
          </Button>
        </div>

        {/* Counter */}
        <div className="mt-4 flex items-center gap-3 text-xs">
          <div className="flex items-center gap-1.5 bg-white/10 rounded-full px-3 py-1.5">
            <Users className="w-3.5 h-3.5" />
            <span><span className="font-bold">{rewarded}</span> rewarded</span>
          </div>
          {pending > 0 && (
            <div className="flex items-center gap-1.5 bg-white/10 rounded-full px-3 py-1.5">
              <span><span className="font-bold">{pending}</span> pending</span>
            </div>
          )}
          <div className="ml-auto text-purple-200 text-xs">
            {remaining > 0 ? `${remaining} of ${data.cap} remaining` : "Cap reached"}
          </div>
        </div>
      </div>
    </div>
  );
}
